/** Pure logic for the transaction-picker's collapsible filter row:
 *  turning the form's raw strings into `searchTransactions` params. */

export type AmountToleranceMode = 'exact' | 'dollar' | 'pct2';

export interface TransactionSearchFilters {
  amount: string;
  toleranceMode: AmountToleranceMode;
  dateFrom: string;
  dateTo: string;
  glAccount: string;
}

export const EMPTY_FILTERS: TransactionSearchFilters = {
  amount: '',
  toleranceMode: 'exact',
  dateFrom: '',
  dateTo: '',
  glAccount: '',
};

function parseAmount(amount: string): number | null {
  const value = Number(amount.trim());
  return amount.trim().length > 0 && Number.isFinite(value) && value > 0 ? value : null;
}

/** Absolute tolerance (cents-precision decimal string) for the amount
 *  filter, or undefined for exact matching. `pct2` computes 2% of the
 *  amount client-side so the API only ever sees absolute bands. */
export function amountToleranceFor(
  amount: string,
  mode: AmountToleranceMode,
): string | undefined {
  const value = parseAmount(amount);
  if (value === null || mode === 'exact') return undefined;
  if (mode === 'dollar') return '1.00';
  return (Math.round(value * 2) / 100).toFixed(2);
}

/** Query params for the active filters. Empty/invalid fields are
 *  omitted, so an untouched filter row leaves the request unchanged. */
export function searchFilterParams(filters: TransactionSearchFilters): {
  amount?: string;
  amount_tolerance?: string;
  date_from?: string;
  date_to?: string;
  gl_account?: string;
} {
  const params: ReturnType<typeof searchFilterParams> = {};
  const value = parseAmount(filters.amount);
  if (value !== null) {
    params.amount = value.toFixed(2);
    const tolerance = amountToleranceFor(filters.amount, filters.toleranceMode);
    if (tolerance !== undefined) params.amount_tolerance = tolerance;
  }
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.glAccount.trim()) params.gl_account = filters.glAccount.trim();
  return params;
}

export function hasActiveFilters(filters: TransactionSearchFilters): boolean {
  return Object.keys(searchFilterParams(filters)).length > 0;
}
